import { Search } from '@orgus/ui/icons';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { openPalette } from '../../lib/commands.ts';
import { useCrumbs, viewLabel } from '../../lib/crumbs.ts';
import { documentTitle, isInstalledApp } from '../../lib/installedApp.ts';
import { Kbd } from '@orgus/ui';
import { UserMenu } from '../../features/auth/UserMenu.tsx';
import { BackToOrigin, Breadcrumbs } from './Breadcrumbs.tsx';
import { ConnectionIndicator } from './ConnectionIndicator.tsx';
import { TOP_BAR_ITEMS } from './topBarItems.ts';
import { usePageTitle } from './useOrigin.ts';

/** The top bar's action slot element, set once it mounts; PageActions re-renders when it appears. */
const useSlot = create<{ el: HTMLElement | null }>()(() => ({ el: null }));
const setSlot = (el: HTMLDivElement | null) => {
  if (useSlot.getState().el !== el) useSlot.setState({ el });
};

/**
 * Put a page's controls in the top bar, beside the page title (one primary action at most).
 *   <PageActions><Button variant="primary" shortcut={['n']}>New session</Button></PageActions>
 */
export function PageActions({ children }: { children: ReactNode }) {
  const el = useSlot((s) => s.el);
  return el ? createPortal(children, el) : null;
}

/** The slim top bar: Back to the origin, the breadcrumb (the page title is the only h1), page actions, search, the bell (notifications and notices), connection. */
export function TopBar() {
  const heading = usePageTitle();
  const view = viewLabel(heading, useCrumbs());

  useEffect(() => {
    document.title = documentTitle(view === heading ? heading : `${view} · ${heading}`, isInstalledApp());
  }, [heading, view]);

  return (
    <header className="flex h-(--layout-topbar-height) shrink-0 items-center gap-3 px-6 max-md:gap-2 max-md:px-4">
      <BackToOrigin />
      <Breadcrumbs title={heading} />
      {/*
        One line, never a cut-off button: the zero-width ::before holds the first line, so an
        action that does not fit wraps to a second line that stays hidden (the palette and the
        page's own shortcut still reach it). On a phone the breadcrumb takes the room.
      */}
      <div ref={setSlot} className="flex h-control min-w-0 flex-1 max-md:hidden flex-wrap items-center justify-end gap-2 overflow-hidden px-1 before:h-control before:w-0" />
      <button
        type="button"
        onClick={() => openPalette()}
        aria-keyshortcuts="Control+K Meta+K"
        // Icon only below 1280 px, so the page's actions always have room (they are never clipped at 1024).
        // The look of a small Input (control-field): a sunken well, the control edge, the accent focus ring.
        className="control-field group hidden size-control-sm shrink-0 items-center justify-center gap-2 text-caption text-fg-subtle hover:text-fg-muted md:flex xl:w-56 xl:justify-start xl:pl-2 xl:pr-1"
      >
        <Search className="size-3.5 shrink-0" aria-hidden />
        {/* The name is the visible words (WCAG 2.5.3, label in name): read aloud below 1280 px too, shown from it. */}
        <span className="sr-only flex-1 text-left xl:not-sr-only">Search or jump to</span>
        <span className="hidden xl:flex">
          <Kbd keys={['mod', 'k']} />
        </span>
      </button>
      {TOP_BAR_ITEMS.map((i) => (
        <i.Component key={i.id} />
      ))}
      <ConnectionIndicator />
      <UserMenu />
    </header>
  );
}
